import { roundM } from './currency';

/**
 * The money arithmetic behind the appointment payment dialog.
 *
 * It lives here rather than inside the component because it is the part that
 * can be wrong in a way nobody notices until a shift is short: the server
 * recomputes the invoice from the session's own rows and refuses the sale
 * unless Σ payments equals ITS total to within one currency unit. Everything
 * below exists to make the two agree.
 */

export interface TenderSplit {
  code: number;
  name: string;
  amount: number;
  isCash: boolean;
}

export interface PricedLine {
  qty: number;
  unit_price: number;
}

/**
 * One "effectively zero" threshold for every money comparison: a single unit of
 * the tenant's currency, capped at a cent. A hardcoded 0.01 reads a 0.008 KWD
 * balance as nothing left to collect.
 */
export function tenderEpsilon(precision: number): number {
  return Math.min(0.01, 10 ** -precision);
}

/**
 * Price the appointment the way `convertToSale` does: round EACH line
 * (`qty × unit_price`), then round the sum, then the VAT, then the total.
 * Summing raw products and rounding once at the end drifts by a unit on a long
 * service list, and that unit is a rejected sale.
 */
export function priceAppointment(
  lines: PricedLine[],
  taxPercent: number,
): { subtotal: number; tax: number; total: number } {
  const subtotal = roundM(lines.reduce((a, l) => a + roundM(l.qty * l.unit_price), 0));
  const tax = roundM((subtotal * taxPercent) / 100);
  return { subtotal, tax, total: roundM(subtotal + tax) };
}

/**
 * Trim the over-tender off the LAST cash split so Σ splits lands exactly on the
 * total.
 *
 * 100 handed over for a 95.50 invoice goes on the wire as a 95.50 cash row plus
 * `cash_tendered: 100` — never as a 100 row, which the server rejects with
 * PAYMENT_SUM_MISMATCH. Returns the splits untouched when there is nothing to
 * trim, or when no cash split exists to absorb it (the dialog blocks confirming
 * in that case, because change cannot come out of a card).
 */
export function trimOverTender(
  splits: TenderSplit[],
  total: number,
  epsilon: number,
): TenderSplit[] {
  const sum = roundM(splits.reduce((a, s) => a + s.amount, 0));
  const over = roundM(sum - total);
  if (over <= epsilon) return splits;
  let lastCash = -1;
  for (let i = splits.length - 1; i >= 0; i--) {
    if (splits[i]!.isCash) {
      lastCash = i;
      break;
    }
  }
  if (lastCash === -1) return splits;
  const out = [...splits];
  out[lastCash] = { ...out[lastCash]!, amount: roundM(out[lastCash]!.amount - over) };
  return out;
}

/**
 * Turn the receptionist's splits into the wire tender the conversion accepts.
 *
 * `cash_tendered` is `total + overage`, NOT the sum of the cash rows. The
 * server derives change as `cash_tendered − Σ payments` and refuses a tender
 * below Σ payments, so on a mixed receipt — 40 on a card, 70 in cash, 95.50
 * due — sending the 70 alone reads as paying 70 against a 95.50 bill and is
 * rejected with CASH_TENDERED_TOO_LOW. What is true either way is that the
 * customer is owed the overage, and that is what `total + overage` encodes.
 *
 * `cashTendered` is null when nothing was over-tendered: the field is optional
 * and the server defaults it to Σ payments, so sending it adds nothing.
 */
export function buildConvertTender(
  splits: TenderSplit[],
  total: number,
  epsilon: number,
): { payments: Array<{ type_code: number; amount: number }>; cashTendered: number | null } {
  const sum = roundM(splits.reduce((a, s) => a + s.amount, 0));
  const over = roundM(Math.max(0, sum - total));
  const exact = trimOverTender(splits, total, epsilon);
  return {
    payments: exact.map((s) => ({ type_code: s.code, amount: s.amount })),
    cashTendered: over > epsilon ? roundM(total + over) : null,
  };
}

/**
 * The notes a receptionist is most likely to be handed for this balance. Same
 * ladder as the till, so the two counters behave alike.
 */
export function quickAmounts(target: number): number[] {
  if (target <= 0) return [];
  const candidates = [
    Math.ceil(target),
    Math.ceil(target / 5) * 5,
    Math.ceil(target / 10) * 10,
    Math.ceil(target / 20) * 20,
    Math.ceil(target / 50) * 50,
  ];
  return [...new Set(candidates)].filter((n) => n >= target).slice(0, 4);
}
